import type { EntityTable, IDType, InsertType, UpdateSpec } from "dexie";
import { create } from "zustand";
import type { BaseResult, BaseSearch } from "./types";

type SearchStoreConfig<S extends BaseSearch, R extends BaseResult> = {
  searchesTable: () => EntityTable<S, "id">;
  resultsTable: () => EntityTable<R, "id">;
};

type SearchStore<S extends BaseSearch, R extends BaseResult> = {
  searches: S[];
  results: Map<string, R>;
  onSearchReady: ((searchId: string) => void) | null;
  add: (search: S) => Promise<void>;
  update: (id: string, patch: Partial<S>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  addResult: (result: R) => Promise<void>;
  getResult: (searchId: string) => R | undefined;
  loadFromDb: () => Promise<void>;
  getPendingSearches: () => S[];
  setOnSearchReady: (cb: ((searchId: string) => void) | null) => void;
};

export function createSearchStore<S extends BaseSearch, R extends BaseResult>(
  config: SearchStoreConfig<S, R>,
) {
  return create<SearchStore<S, R>>((set, get) => ({
    searches: [],
    results: new Map(),
    onSearchReady: null,

    async add(search: S) {
      await config.searchesTable().add(search);
      set((state) => ({
        searches: [...state.searches, search],
      }));
    },

    async update(id: string, patch: Partial<S>) {
      const updatedPatch = { ...patch, updatedAt: new Date() };
      await config
        .searchesTable()
        .update(
          id as IDType<S, "id">,
          updatedPatch as UpdateSpec<InsertType<S, "id">>,
        );
      set((state) => ({
        searches: state.searches.map((search) =>
          search.id === id ? ({ ...search, ...updatedPatch } as S) : search,
        ),
      }));
    },

    async remove(id: string) {
      await config.searchesTable().delete(id as IDType<S, "id">);
      await config.resultsTable().where("searchId").equals(id).delete();
      set((state) => {
        const newResults = new Map(state.results);
        newResults.delete(id);
        return {
          searches: state.searches.filter((search) => search.id !== id),
          results: newResults,
        };
      });
    },

    async addResult(result: R) {
      await config.resultsTable().add(result);
      set((state) => {
        const newResults = new Map(state.results);
        newResults.set(result.searchId, result);
        return { results: newResults };
      });
    },

    getResult(searchId: string) {
      return get().results.get(searchId);
    },

    async loadFromDb() {
      const searches = await config.searchesTable().toArray();
      const resultsArray = await config.resultsTable().toArray();
      const results = new Map<string, R>();

      for (const result of resultsArray) {
        results.set(result.searchId, result);
      }

      set({ searches, results });
    },

    getPendingSearches() {
      return get().searches.filter(
        (search) =>
          search.status === "submitting" || search.status === "waiting",
      );
    },

    setOnSearchReady(cb) {
      set({ onSearchReady: cb });
    },
  }));
}
